import React, { useState, useEffect, useContext } from 'react';
import { encode } from '@/base64';
import { newUid, deep_copy, getCharForNumber } from '@/util';
import { useTranslation } from 'react-i18next';
import { uploadImage } from '@/query/documents';
import { Button, IconButton, Switch, InputTextarea } from '@teo/components';
import { Plus, Cross } from '@teo/components/icons';
import UploadDropzoneImage from '@/components/UploadDropzoneImage/UploadDropzoneImage';
import { useDrop, useDrag } from 'react-dnd';
import clsx from 'clsx';
import { QuestionSection } from '../QuestionSection';
import retry from 'async-await-retry';
import { NotificationContext } from '@/components/Contexts/NotificationContext';
import FroalaTextAreaEdit from '@/components/Froala/FroalaTextAreaEdit';
import { LibraryModal } from '@/components/LibraryModal/LibraryModal';

export const initialImageShowParts = () => ({
  noSubmit: true,
  title: '',
  image: null,
  labels: [{ uid: newUid(20), x: 0.1, y: 0.1, tx: 0, ty: 0, text: '' }],
  type: 'ImageShowParts',
  interactiveType: true,
});

export const pastels = [
  '#C25FF4',
  '#E3BF00',
  '#6EC6C0',
  '#F25850',
  '#29BAFF',
  '#AE7226',
  '#1173DD',
  '#9AD055',
  '#807EFF',
  '#FF8EA4',
];

const TEO_ALLOW_WIDGET_DROP = '__teoAllowWidgetDrop';

const allowWidgetDropThroughPlaceholderGuard = (ev) => {
  ev.nativeEvent[TEO_ALLOW_WIDGET_DROP] = true;
};

export const ImageShowPartsEdit = ({
  state,
  index,
  onModified = undefined,
  setStateFn = undefined,
}) => {
  const { setShowNotification } = useContext(NotificationContext);
  const { t } = useTranslation();
  const [title, setTitle] = useState(state.title);
  const [labels, setLabels] = useState(state.labels);
  const [img, setImg] = useState(state.image);
  const [interactiveType, setInteractiveType] = useState(state.interactiveType);
  const [dragging, setDragging] = useState(false);

  const [modified, setModified] = useState(newUid(20));
  const [immediateFeedback, setImmediateFeedback] = useState(
    !!state.immediateFeedback
  );
  const [isOpenLibraryModal, setIsOpenLibraryModal] = useState(false);

  useEffect(() => {
    onLoadImg();
  }, [labels]);

  //generate the state
  const getState = async () => {
    let newState = deep_copy(state);
    newState.immediateFeedback = immediateFeedback;
    await retry(() => {
      let questionNode = document.querySelector(
        `#${CSS.escape(state.uid)} .wg-question .question_optional`
      );
      if (questionNode) {
        newState.title = questionNode.innerHTML;
        return Promise.resolve();
      }
      return Promise.reject();
    });
    newState.labels = labels;
    newState.image = img;
    newState.interactiveType = interactiveType;

    return newState;
  };
  setStateFn && setStateFn(getState);

  // useEffect(() => {
  //   onModified && onModified();
  // }, [onModified, modified]);

  const onLoadImg = () => {
    const canvas = document.getElementById(state.uid + '_canvas');
    const img = document.getElementById(state.uid + '_img');

    if (!img || !canvas) return;

    canvas.width = img.width * 5;
    canvas.height = img.height * 5;

    const ctx = canvas.getContext('2d');

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.lineWidth = 20;
    labels.forEach((x, index) => {
      //const angle = Math.atan2(x.y - x.ty, x.x - x.tx); //angle it approaches the label

      const label = document.getElementById(`${state.uid}_label_${index}`);
      let bbox = label.getBoundingClientRect();
      const endY = (x.y + bbox.height / 2 / img.height) * canvas.height;
      //const endX = (x.x + (bbox.width / 2) / img.width) * canvas.width;
      const endX = (x.x + 15 / img.width) * canvas.width;
      ctx.beginPath();
      const startX = x.tx * canvas.width;
      const startY = x.ty * canvas.height;
      //const endX *= canvas.width;
      //const endY *= canvas.height;
      ctx.moveTo(startX, startY);
      ctx.lineTo(endX, endY);
      ctx.strokeStyle = pastels[index % pastels.length];
      ctx.stroke();
      ctx.closePath();
    });
  };

  const DropZone = () => {
    const [{ canDrop, isOver }, drop] = useDrop(() => ({
      accept: ['label', 'dot'],
      drop: (item, monitor) => {
        const type = monitor.getItemType();
        if (type === 'label') {
          const delta = monitor.getDifferenceFromInitialOffset();
          let newLabels = deep_copy(labels);
          const img = document.getElementById(state.uid + '_img');
          newLabels[item.index].y = item.y + delta.y / img.height;
          newLabels[item.index].x = item.x + delta.x / img.width;
          setLabels(newLabels);
        } else if (type === 'dot') {
          const delta = monitor.getDifferenceFromInitialOffset();
          let newLabels = deep_copy(labels);
          const img = document.getElementById(state.uid + '_img');
          newLabels[item.index].ty = item.ty + delta.y / img.height;
          newLabels[item.index].tx = item.tx + delta.x / img.width;
          setLabels(newLabels);
        }
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    }));

    return (
      <div
        className="absolute h-full w-full"
        style={{
          pointerEvents: dragging ? 'auto' : 'none',
          top: 0,
          left: 0,
        }}
        ref={drop}
      ></div>
    );
  };

  const Label = (props) => {
    const [{ isDragging }, drag] = useDrag(() => ({
      type: 'label',
      item: props,
      canDrag: true,
      collect: (monitor) => {
        return {
          isDragging: monitor.isDragging(),
          handlerId: monitor.getHandlerId(),
        };
      },
      end: () => {
        setDragging(false);
      },
    }));

    if (isDragging) setDragging('label_' + props.uid);
    const opacity = isDragging || 'label_' + props.uid === dragging ? 0.4 : 1;
    return (
      <div
        ref={drag}
        key={`${state.uid}_isDragging`}
        id={`${state.uid}_label_${props.index}`}
        className={clsx(
          'fr-draggable absolute flex items-center bg-grey-01 p-1'
        )}
        style={{
          top: props.y * 100 + '%',
          left: props.x * 100 + '%',
          opacity: opacity,
          pointerEvents: dragging ? 'none' : 'all',
          borderRadius: '4px',
        }}
      >
        <img
          src={`/images/icons/drug_icon.svg`}
          className="ml-1 mr-1 h-[14px] w-[14px] bg-grey-01 text-grey-01"
          alt="icon"
          style={{ pointerEvents: 'none' }}
        />
        <div
          className="rounded-md px-2.5 pb-1 pt-0.5 text-white"
          style={{
            backgroundColor: `${pastels[props.index]}`,
          }}
        >
          {getCharForNumber(props.index)}
        </div>
        {/* <InputText
          style={{
            border: 'none',
            backgroundColor: '#f8f8f9',
          }}
          id="labelContainer"
          onChange={({ target: { value } }) => {
            texts[props.uid] = value;
          }}
          onBlur={(ev) => {
            let newLabels = deep_copy(labels);
            newLabels[props.index].text = ev.target.value;
            setLabels(newLabels);
          }}
          defaultValue={props.text}
        /> */}
        <IconButton
          Icon={Cross}
          ariaLabel="delete order"
          onClick={() => {
            let newLabels = deep_copy(labels);
            newLabels.splice(props.index, 1);
            setLabels(newLabels);
          }}
          variant="ghost"
          className="text-#1e1e2052 bg-grey-01"
          style={{ color: '#1e1e2052' }}
          size="sm"
        />
      </div>
    );
  };

  const Dot = (props) => {
    const [{ isDragging }, drag] = useDrag(() => ({
      type: 'dot',
      item: props,
      canDrag: true,
      collect: (monitor) => {
        return {
          isDragging: monitor.isDragging(),
          handlerId: monitor.getHandlerId(),
        };
      },
      end: () => {
        setDragging(false);
      },
    }));

    if (isDragging) setDragging('dot_' + props.uid);
    const opacity = isDragging || 'dot_' + props.uid === dragging ? 0.4 : 1;
    return (
      <span
        ref={drag}
        className="fr-draggable absolute h-3.5 w-3.5 cursor-move rounded-full border-4"
        style={{
          transform: 'translate(-50%, -50%)',
          top: props.ty * 100 + '%',
          left: props.tx * 100 + '%',
          opacity: opacity,
          pointerEvents: dragging ? 'none' : 'all',
          borderColor: `${pastels[props.index % pastels.length]}`,
        }}
      ></span>
    );
  };

  const handleChangeContent = (uid) => {
    setTimeout(() => {
      let textNode = document.querySelector(
        `#${CSS.escape(state.uid)} .wg-content-${uid} .question_optional`
      );
      setLabels((prev) =>
        prev?.map((el) =>
          el?.uid == uid ? { ...el, text: textNode.innerHTML } : el
        )
      );
    }, 200);
  };

  return (
    <div
      data-open="SOW"
      className={`flex w-full flex-col gap-2 lg:p-2`}
      style={{ maxWidth: 'calc(100vw - 32px)' }}
      data-state={encode(JSON.stringify(state))}
      id={state.uid}
    >
      <div className="wg-question">
        <QuestionSection questionVraag={title} title={'test'} />
      </div>
      <div>
        <div className="my-5 flex w-full flex-row items-center sm:w-auto">
          <Switch
            checked={interactiveType}
            onChange={(isCheck) => {
              setInteractiveType(isCheck);
            }}
          />
          <div className="mr-4 px-4">{t('widgets.edit.dynamic_content')}</div>
        </div>
      </div>
      {!img ? (
        <div className="flex flex-col items-center justify-center rounded-lg bg-grey-02 px-3 py-8">
          <div className="flex items-center">
            <img src="/images/icons/image.svg" alt="image" className="mr-3" />
            <span className="text-base font-black font-medium">
              {t('widgets.type.add_image')}
            </span>
          </div>

          <div className="my-4 text-sm font-normal text-grey-08">
            {t('widgets.type.uploade_or_select')}
          </div>

          <Button
            variant="outline"
            size="md"
            onClick={() => {
              setIsOpenLibraryModal(true);
            }}
            className="bg-grey-02"
          >
            {t('widgets.type.select_image')}
          </Button>
        </div>
      ) : (
        <>
          <div
            className="relative h-full w-full rounded-md"
            contentEditable="false"
            onDropCapture={allowWidgetDropThroughPlaceholderGuard}
            onDragOverCapture={allowWidgetDropThroughPlaceholderGuard}
          >
            <img
              src={img}
              id={state.uid + '_img'}
              onLoad={onLoadImg}
              className="pointer-events-none w-full !cursor-default rounded-md"
              draggable="false"
            />
            <canvas
              id={state.uid + '_canvas'}
              style={{
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
              }}
              className="pointer-events-none absolute"
            ></canvas>
            <div
              className="absolute rounded-full bg-white"
              style={{
                backgroundColor: 'white',
                color: '#fdbe06',
                bottom: '10px',
                right: '10px',
              }}
            >
              <IconButton
                Icon={Plus}
                variant="secondary"
                size="lg"
                className="!rounded-full bg-white !p-5 text-primary-05 shadow-[0_0_12px_-6px_black]"
                onClick={() => {
                  let newLabels = deep_copy(labels);
                  newLabels.push({
                    uid: newUid(20),
                    x: 0.1,
                    y: 0.1,
                    tx: 0,
                    ty: 0,
                    text: '',
                  });
                  setLabels(newLabels);
                }}
              />
            </div>
            <DropZone />
            {labels.map((x, index) => (
              <>
                <Label
                  key={'label_' + state.uid + x.index}
                  {...x}
                  index={index}
                ></Label>
                <Dot
                  key={'dot_' + state.uid + x.index}
                  {...x}
                  index={index}
                ></Dot>
              </>
            ))}
          </div>
          <div>
            {labels?.map((x, index) => (
              <div
                key={index}
                className={`relative mb-3 flex flex-row items-center`}
              >
                <div
                  className="mr-2 rounded-md px-2.5 pb-1 pt-0.5 text-white"
                  style={{
                    backgroundColor: `${pastels[index]}`,
                  }}
                >
                  {getCharForNumber(index)}
                </div>
                <div
                  className={`wg-content-${labels[index]?.uid} bg-grey-0 w-full rounded-lg border px-4 py-3`}
                  onBlur={() => handleChangeContent(labels[index]?.uid)}
                >
                  <QuestionSection questionVraag={x.text} imgContent={true} />
                </div>
              </div>
            ))}
          </div>

          <div className=" flex flex-row items-center border-b border-grey-02 pb-4">
            <Button
              variant="outline"
              onClick={() => {
                setIsOpenLibraryModal(true);
              }}
            >
              {t('widgets.widgets_edit.replace_image')}
            </Button>
          </div>
        </>
      )}

      {isOpenLibraryModal && (
        <LibraryModal
          setIsOpenLibraryModal={setIsOpenLibraryModal}
          isOpenLibraryModal={isOpenLibraryModal}
          lessonPage={false}
          setUploadFile={(img) => {
            setImg(img);
          }}
          onSelectImage={(img) => {
            setImg(img);
          }}
        />
      )}

      <div data-closed="EOW"></div>
    </div>
  );
};
